import { List, Section, Text } from '@/shared/ui/primitives';
import type { FC } from 'react';
import { useMemo, useState } from 'react';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useParams } from 'react-router';

import { Page } from '@/app/Page';
import { clientsQueryOptions } from '@/features/clients';
import {
  bucketPasses,
  PassModal,
  PastPassListItem,
  passDraftToUpdateRequest,
  useDeletePass,
  useUpdatePass,
} from '@/features/passes';
import { QueryBoundary, QueryErrorPlaceholder } from '@/shared/ui';
import { TEST_IDS } from '@/testIds';
import type { ClientDetailPass, Pass } from '@/types';
import { ClientSubPageLayout } from './client-profile-page/ClientSubPageLayout';
import styles from './ClientPastPassesPage.module.css';

export const ClientPastPassesPage: FC = () => {
  const { clientId } = useParams<{ clientId: string }>();

  if (!clientId) {
    return (
      <Page back={true}>
        <QueryErrorPlaceholder header="Not found" />
      </Page>
    );
  }

  return (
    <Page back={true}>
      <QueryBoundary resetKeys={[clientId]}>
        <ClientPastPassesPageContent clientId={clientId} />
      </QueryBoundary>
    </Page>
  );
};

interface ClientPastPassesPageContentProps {
  clientId: string;
}

const ClientPastPassesPageContent: FC<ClientPastPassesPageContentProps> = ({ clientId }) => {
  const { data: client } = useSuspenseQuery(clientsQueryOptions.detail(clientId));

  const past = useMemo(() => bucketPasses(client.passes).past, [client.passes]);

  const [editingPass, setEditingPass] = useState<ClientDetailPass | undefined>();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const updatePassMutation = useUpdatePass({
    onSuccess: () => {
      setIsEditModalOpen(false);
      setEditingPass(undefined);
    },
  });
  const deletePassMutation = useDeletePass();

  const handlePassClick = (pass: ClientDetailPass) => {
    setEditingPass(pass);
    setIsEditModalOpen(true);
  };

  const handleEditPass = async (passData: Omit<Pass, 'id'>): Promise<void> => {
    if (!editingPass) return;
    await updatePassMutation.mutateAsync({
      id: editingPass.id,
      data: passDraftToUpdateRequest(passData),
      clientId: client.id,
    });
  };

  const handleDeletePass = async (id: string): Promise<void> => {
    await deletePassMutation.mutateAsync({ id, clientId: client.id });
  };

  return (
    <ClientSubPageLayout
      title="Past passes"
      subtitle={client.name}
      titleTestId={TEST_IDS.pastPassesPageTitle}
    >
      {past.length === 0 ? (
        <div className={styles.empty} data-testid={TEST_IDS.pastPassesPageEmpty}>
          <Text weight="regular">No past passes yet</Text>
        </div>
      ) : (
        <List>
          <Section>
            {past.map((pass) => (
              <PastPassListItem key={pass.id} pass={pass} onClick={handlePassClick} />
            ))}
          </Section>
        </List>
      )}

      <PassModal
        isOpen={isEditModalOpen}
        mode="edit"
        onClose={() => {
          setIsEditModalOpen(false);
          setEditingPass(undefined);
        }}
        onSave={handleEditPass}
        onDelete={handleDeletePass}
        editPass={editingPass}
        isLoading={updatePassMutation.isPending || deletePassMutation.isPending}
      />
    </ClientSubPageLayout>
  );
};
